import {
    createContext,
    CSSProperties,
    HTMLAttributes,
    Key,
    ReactNode,
    UIEvent,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Col, Flex, Row, RowProps } from 'antd';
import type { GlobalToken } from 'antd/es/theme/interface';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import OverflowFadeScrollArea, { OverflowFadeSurface } from '@/shared/components/layouts/OverflowFadeScrollArea';
import ListItemSkeleton from '@/shared/components/skeletons/ListItemSkeleton';

type VirtualizedListSize = 'small' | 'default' | 'large';
type VirtualizedListItemLayout = 'horizontal' | 'vertical';

interface VirtualizedListProps<T> extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
    bordered?: boolean;
    children?: ReactNode;
    dataSource?: T[];
    footer?: ReactNode;
    grid?: {
        gutter?: RowProps['gutter'];
        column?: number;
        xs?: number;
        sm?: number;
        md?: number;
        lg?: number;
        xl?: number;
        xxl?: number;
    };
    endReachedThreshold?: number;
    emptyStyle?: CSSProperties;
    header?: ReactNode;
    headerStyle?: CSSProperties;
    hasMore?: boolean;
    itemLayout?: VirtualizedListItemLayout;
    items?: T[];
    estimateSize?: number;
    height?: string | number;
    loadMore?: ReactNode;
    // This remains compatible with Ant Design's legacy spinning configuration.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    loading?: boolean | Record<string, any>;
    loadingItemCount?: number;
    loadingMore?: boolean;
    locale?: { emptyText?: ReactNode };
    onEndReached?: () => void | Promise<void>;
    overscan?: number;
    overflowFade?: boolean;
    overflowFadeSurface?: OverflowFadeSurface;
    overflowX?: CSSProperties['overflowX'];
    pageSize?: number;
    paginate?: boolean;
    renderItem?: (item: T, index: number) => ReactNode;
    renderLoadingItem?: (index: number) => ReactNode;
    getItemKey?: (item: T, index: number) => string | number;
    rowKey?: ((item: T) => Key) | keyof T;
    rootClassName?: string;
    size?: VirtualizedListSize;
    split?: boolean;
    style?: CSSProperties;
    innerStyle?: CSSProperties;
    // Preserve passthrough compatibility with the list API while consumers migrate to explicit props.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any;
}

interface VirtualizedListItemProps extends HTMLAttributes<HTMLDivElement> {
    actions?: ReactNode[];
    children?: ReactNode;
    classNames?: Partial<Record<'actions' | 'extra', string>>;
    extra?: ReactNode;
    styles?: Partial<Record<'actions' | 'extra', CSSProperties>>;
}

interface VirtualizedListItemMetaProps {
    avatar?: ReactNode;
    children?: ReactNode;
    className?: string;
    description?: ReactNode;
    style?: CSSProperties;
    title?: ReactNode;
}

type VirtualizedListItemComponent = ((props: VirtualizedListItemProps) => JSX.Element) & {
    Meta: (props: VirtualizedListItemMetaProps) => JSX.Element;
};

type VirtualizedListComponent = (<T>(props: VirtualizedListProps<T>) => JSX.Element) & {
    Item: VirtualizedListItemComponent;
};

type VirtualizedListStyleContextValue = {
    bordered: boolean;
    grid: boolean;
    hasTrailingContent: boolean;
    itemLayout: VirtualizedListItemLayout;
    size: VirtualizedListSize;
    split: boolean;
};

type VirtualizedListItemContextValue = {
    isLast: boolean;
    remeasure: (element?: HTMLElement | null) => void;
};

const VirtualizedListStyleContext = createContext<VirtualizedListStyleContextValue>({
    bordered: false,
    grid: false,
    hasTrailingContent: false,
    itemLayout: 'horizontal',
    size: 'default',
    split: true,
});

const VirtualizedListItemContext = createContext<VirtualizedListItemContextValue>({
    isLast: false,
    remeasure: () => undefined,
});

// This colocated hook intentionally exposes the private per-item list context.
// eslint-disable-next-line react-refresh/only-export-components
export const useVirtualizedListItemRemeasure = () => useContext(VirtualizedListItemContext).remeasure;

const cx = (...classes: Array<string | false | null | undefined>) => classes.filter(Boolean).join(' ');

const unit = (value?: number | string) => (typeof value === 'number' ? `${value}px` : value);

const getListItemPadding = (token: GlobalToken, size: VirtualizedListSize, bordered: boolean) => {
    const defaultPadding = `${unit(token.paddingContentVertical)} ${bordered ? unit(token.paddingLG) : 0}`;
    const smallPadding = `${unit(token.paddingContentVerticalSM)} ${unit(token.paddingContentHorizontal)}`;
    const largePadding = `${unit(token.paddingContentVerticalLG)} ${unit(token.paddingContentHorizontalLG)}`;

    if (size === 'small') {
        return smallPadding;
    }

    if (size === 'large') {
        return largePadding;
    }

    return defaultPadding;
};

const getGridColumnCount = (grid: VirtualizedListProps<unknown>['grid']) =>
    grid?.xxl ?? grid?.xl ?? grid?.lg ?? grid?.md ?? grid?.sm ?? grid?.xs ?? grid?.column ?? 1;

const VirtualizedList = (<T,>({
    bordered = false,
    children,
    className,
    dataSource,
    endReachedThreshold = 240,
    emptyStyle,
    estimateSize = 72,
    footer,
    getItemKey,
    grid,
    hasMore = true,
    header,
    headerStyle,
    height,
    innerStyle,
    itemLayout = 'horizontal',
    items,
    loadMore,
    loading = false,
    loadingItemCount = 6,
    loadingMore = false,
    locale,
    onEndReached,
    onScroll,
    overscan = 10,
    overflowFade = false,
    overflowFadeSurface = 'container',
    overflowX = 'auto',
    pageSize = 50,
    paginate = false,
    renderItem,
    renderLoadingItem,
    rootClassName,
    rowKey,
    size = 'default',
    split = true,
    style,
    ...props
}: VirtualizedListProps<T>): JSX.Element => {
    const parentRef = useRef<HTMLDivElement>(null);
    const endReachedLockRef = useRef(false);
    const baseItems = items ?? dataSource ?? [];
    const [visibleCount, setVisibleCount] = useState(pageSize);
    const clientHasMore = paginate && visibleCount < baseItems.length;
    const resolvedItems = paginate ? baseItems.slice(0, visibleCount) : baseItems;
    const trailingLoadingItemCount = loadingMore && renderLoadingItem ? Math.max(0, Math.trunc(loadingItemCount)) : 0;
    const virtualItemCount = resolvedItems.length + trailingLoadingItemCount;
    const { token } = useAntDesignTheme();

    useEffect(() => {
        if (paginate) {
            setVisibleCount((count) => Math.min(Math.max(count, pageSize), baseItems.length));
        }
    }, [baseItems.length, pageSize, paginate]);

    const maybeLoadMore = (element: HTMLElement) => {
        if ((!onEndReached && !paginate) || loadingMore || endReachedLockRef.current) {
            return;
        }

        const distanceFromEnd = element.scrollHeight - element.scrollTop - element.clientHeight;

        if (distanceFromEnd > endReachedThreshold) {
            return;
        }

        endReachedLockRef.current = true;

        if (clientHasMore) {
            setVisibleCount((count) => Math.min(count + pageSize, baseItems.length));
            window.setTimeout(() => {
                endReachedLockRef.current = false;
            }, 250);

            return;
        }

        if (onEndReached) {
            if (!hasMore) {
                endReachedLockRef.current = false;

                return;
            }

            Promise.resolve(onEndReached()).finally(() => {
                window.setTimeout(() => {
                    endReachedLockRef.current = false;
                }, 250);
            });

            return;
        }

        endReachedLockRef.current = false;
    };

    const handleScroll = (event: UIEvent<HTMLDivElement>) => {
        onScroll?.(event);
        maybeLoadMore(event.currentTarget);
    };

    const getKey = (item: T, index: number, fallback: Key = index) => {
        if (getItemKey) {
            return getItemKey(item, index);
        }

        if (typeof rowKey === 'function') {
            return rowKey(item);
        }

        if (rowKey && item && typeof item === 'object') {
            const candidate = (item as Record<PropertyKey, unknown>)[rowKey as PropertyKey];

            return typeof candidate === 'string' || typeof candidate === 'number' ? candidate : fallback;
        }

        if (item && typeof item === 'object' && 'key' in item) {
            return (item as { key?: Key }).key ?? fallback;
        }

        return fallback;
    };

    const hasTrailingContent = !!(footer || loadMore);
    const contextValue: VirtualizedListStyleContextValue = {
        bordered,
        grid: !!grid,
        hasTrailingContent,
        itemLayout,
        size,
        split,
    };
    const rootStyle: CSSProperties & Record<string, string | number | undefined> = {
        '--cc-virtualized-list-title-color': token.colorText,
        boxSizing: 'border-box',
        color: token.colorText,
        fontSize: token.fontSize,
        lineHeight: token.lineHeight,
        position: 'relative',
        width: '100%',
        ...(bordered
            ? {
                  border: `${unit(token.lineWidth)} ${token.lineType} ${token.colorBorder}`,
                  borderRadius: token.borderRadiusLG,
              }
            : undefined),
        ...style,
    };
    const headerFooterStyle: CSSProperties = {
        background: 'transparent',
        color: token.colorText,
        paddingBlock: token.paddingSM,
        ...(bordered ? { paddingInline: token.paddingLG } : undefined),
    };
    const resolvedHeaderStyle: CSSProperties = { ...headerFooterStyle, ...headerStyle };
    const itemsStyle: CSSProperties = {
        listStyle: 'none',
        margin: 0,
        padding: 0,
        ...innerStyle,
    };
    const rootClassNames = cx(
        'cc-virtualized-list',
        'ant-list',
        itemLayout === 'vertical' && 'ant-list-vertical',
        size === 'small' && 'ant-list-sm',
        size === 'large' && 'ant-list-lg',
        split && 'ant-list-split',
        bordered && 'ant-list-bordered',
        grid && 'ant-list-grid',
        hasTrailingContent && 'ant-list-something-after-last-item',
        className,
        rootClassName,
    );
    const isLoading = typeof loading === 'boolean' ? loading : Boolean(loading?.spinning);
    const virtualizedListStyles = (
        <style>
            {`
                .cc-virtualized-list .ant-list-item-meta-title > a {
                    color: var(--cc-virtualized-list-title-color) !important;
                }

                .cc-virtualized-list .ant-list-item-meta-title a {
                    color: var(--cc-virtualized-list-title-color) !important;
                }
            `}
        </style>
    );
    const shouldVirtualize = Boolean(height && !children && virtualItemCount > 0 && !grid);
    const rowVirtualizer = useVirtualizer({
        count: shouldVirtualize ? virtualItemCount : 0,
        enabled: shouldVirtualize,
        getScrollElement: () => (shouldVirtualize ? parentRef.current : null),
        estimateSize: () => estimateSize,
        overscan,
    });
    const remeasureVirtualizedItem = useCallback(
        (element?: HTMLElement | null) => {
            if (!shouldVirtualize) {
                return;
            }

            const virtualRow = element?.closest<HTMLElement>('[data-index]');

            if (virtualRow) {
                rowVirtualizer.measureElement(virtualRow);
                return;
            }

            rowVirtualizer.measure();
        },
        [rowVirtualizer, shouldVirtualize],
    );

    const renderListItem = (item: T, index: number, key?: Key) => (
        <VirtualizedListItemContext.Provider
            key={key ?? getKey(item, index)}
            value={{
                isLast: index === virtualItemCount - 1,
                remeasure: remeasureVirtualizedItem,
            }}
        >
            {renderItem?.(item, index)}
        </VirtualizedListItemContext.Provider>
    );

    const renderItems = () => {
        if (resolvedItems.length === 0) {
            return (
                <div
                    style={{
                        color: token.colorTextDisabled,
                        padding: token.padding,
                        textAlign: 'center',
                        ...emptyStyle,
                    }}
                >
                    {locale?.emptyText ?? null}
                </div>
            );
        }

        if (grid) {
            const columnCount = getGridColumnCount(grid);
            const colStyle: CSSProperties = columnCount
                ? { maxWidth: `${100 / columnCount}%`, width: `${100 / columnCount}%` }
                : {};

            return (
                <Row gutter={grid.gutter} style={innerStyle}>
                    {resolvedItems.map((item, index) => (
                        <Col key={getKey(item, index)} flex='none' style={colStyle}>
                            {renderListItem(item, index)}
                        </Col>
                    ))}
                </Row>
            );
        }

        if (!height) {
            return <div style={itemsStyle}>{resolvedItems.map((item, index) => renderListItem(item, index))}</div>;
        }

        return null;
    };

    const loadingSkeleton = (
        <div aria-label='Loading list' style={{ minHeight: height ? unit(height) : '10rem', padding: token.padding }}>
            {Array.from({ length: 6 }, (_, index) => (
                <ListItemSkeleton
                    avatar
                    compact
                    key={index}
                    lineWidths={['74%', '48%']}
                    style={{ padding: `${unit(token.paddingXS)} 0` }}
                />
            ))}
        </div>
    );

    const renderStaticContent = () => (
        <>
            {header && <div style={resolvedHeaderStyle}>{header}</div>}
            {isLoading ? (
                loadingSkeleton
            ) : (
                <>
                    {children}
                    {!children && renderItems()}
                </>
            )}
            {footer && <div style={headerFooterStyle}>{footer}</div>}
            {loadMore}
        </>
    );
    const renderVirtualizedContent = () => (
        <>
            {virtualizedListStyles}
            {header && <div style={resolvedHeaderStyle}>{header}</div>}
            {isLoading ? (
                loadingSkeleton
            ) : (
                <div
                    style={{
                        height: `${rowVirtualizer.getTotalSize()}px`,
                        position: 'relative',
                        width: '100%',
                        ...itemsStyle,
                    }}
                >
                    {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                        if (virtualRow.index >= resolvedItems.length) {
                            const loadingIndex = virtualRow.index - resolvedItems.length;

                            return (
                                <div
                                    key={`loading-${loadingIndex}`}
                                    data-index={virtualRow.index}
                                    ref={rowVirtualizer.measureElement}
                                    style={{
                                        left: 0,
                                        position: 'absolute',
                                        top: 0,
                                        transform: `translateY(${virtualRow.start}px)`,
                                        width: '100%',
                                    }}
                                >
                                    {renderLoadingItem?.(loadingIndex)}
                                </div>
                            );
                        }

                        const item = resolvedItems[virtualRow.index];
                        const key = getKey(item, virtualRow.index, virtualRow.key);

                        return (
                            <div
                                key={key}
                                data-index={virtualRow.index}
                                ref={rowVirtualizer.measureElement}
                                style={{
                                    left: 0,
                                    position: 'absolute',
                                    top: 0,
                                    transform: `translateY(${virtualRow.start}px)`,
                                    width: '100%',
                                }}
                            >
                                {renderListItem(item, virtualRow.index, key)}
                            </div>
                        );
                    })}
                </div>
            )}
            {footer && <div style={headerFooterStyle}>{footer}</div>}
            {loadMore}
        </>
    );

    if (!shouldVirtualize) {
        if (overflowFade) {
            return (
                <VirtualizedListStyleContext.Provider value={contextValue}>
                    <OverflowFadeScrollArea
                        {...props}
                        className={rootClassNames}
                        containerStyle={rootStyle}
                        fill={style?.height !== undefined}
                        onScroll={handleScroll}
                        surface={overflowFadeSurface}
                    >
                        {virtualizedListStyles}
                        {renderStaticContent()}
                    </OverflowFadeScrollArea>
                </VirtualizedListStyleContext.Provider>
            );
        }

        return (
            <VirtualizedListStyleContext.Provider value={contextValue}>
                <div className={rootClassNames} style={rootStyle} onScroll={handleScroll} {...props}>
                    {virtualizedListStyles}
                    {renderStaticContent()}
                </div>
            </VirtualizedListStyleContext.Provider>
        );
    }

    if (overflowFade) {
        return (
            <VirtualizedListStyleContext.Provider value={contextValue}>
                <OverflowFadeScrollArea
                    {...props}
                    className={rootClassNames}
                    containerStyle={{ ...rootStyle, height }}
                    fill
                    onScroll={handleScroll}
                    ref={parentRef}
                    surface={overflowFadeSurface}
                >
                    {renderVirtualizedContent()}
                </OverflowFadeScrollArea>
            </VirtualizedListStyleContext.Provider>
        );
    }

    return (
        <VirtualizedListStyleContext.Provider value={contextValue}>
            <div
                ref={parentRef}
                className={rootClassNames}
                style={{ ...rootStyle, height, overflowX, overflowY: 'auto' }}
                onScroll={handleScroll}
                {...props}
            >
                {renderVirtualizedContent()}
            </div>
        </VirtualizedListStyleContext.Provider>
    );
}) as VirtualizedListComponent;

const VirtualizedListItem = (({
    actions,
    children,
    className,
    classNames,
    extra,
    style,
    styles,
    ...props
}: VirtualizedListItemProps): JSX.Element => {
    const { token } = useAntDesignTheme();
    const { bordered, grid, hasTrailingContent, itemLayout, size, split } = useContext(VirtualizedListStyleContext);
    const { isLast } = useContext(VirtualizedListItemContext);
    const itemPadding = getListItemPadding(token, size, bordered);
    const showBorder = split && !grid && (!isLast || hasTrailingContent);
    const actionSplitHeight = Number(token.fontSize) * Number(token.lineHeight) - Number(token.marginXXS) * 2;
    const itemStyle: CSSProperties = {
        borderBlockEnd: showBorder ? `${unit(token.lineWidth)} ${token.lineType} ${token.colorSplit}` : undefined,
        boxSizing: 'border-box',
        color: token.colorText,
        maxWidth: '100%',
        minWidth: 0,
        padding: grid ? 0 : itemPadding,
        width: '100%',
        ...(grid ? { display: 'block', marginBlockEnd: token.margin } : undefined),
        ...style,
    };
    const actionsContent = actions && actions.length > 0 && (
        <Flex
            component='ul'
            align='center'
            className={cx('ant-list-item-action', classNames?.actions)}
            style={{
                flex: '0 0 auto',
                fontSize: 0,
                listStyle: 'none',
                margin: 0,
                marginInlineStart: itemLayout === 'vertical' ? 'auto' : token.marginXXL,
                padding: 0,
                ...styles?.actions,
            }}
        >
            {actions.map((action, index) => (
                <li
                    key={`action-${index}`}
                    style={{
                        color: token.colorTextDescription,
                        display: 'inline-block',
                        fontSize: token.fontSize,
                        lineHeight: token.lineHeight,
                        padding: `0 ${unit(itemLayout === 'vertical' ? token.padding : token.paddingXS)}`,
                        position: 'relative',
                        textAlign: 'center',
                    }}
                >
                    {action}
                    {index !== actions.length - 1 && (
                        <em
                            style={{
                                backgroundColor: token.colorSplit,
                                height: actionSplitHeight,
                                insetBlockStart: '50%',
                                insetInlineEnd: 0,
                                position: 'absolute',
                                transform: 'translateY(-50%)',
                                width: token.lineWidth,
                            }}
                        />
                    )}
                </li>
            ))}
        </Flex>
    );

    if (itemLayout === 'vertical' && extra) {
        return (
            <Flex
                align='flex-start'
                className={cx('ant-list-item', className)}
                gap={token.marginLG}
                justify='space-between'
                style={itemStyle}
                {...props}
            >
                <div style={{ display: 'block', flex: 1, minWidth: 0 }}>
                    {children}
                    {actionsContent && <div style={{ marginBlockStart: token.padding }}>{actionsContent}</div>}
                </div>
                <div
                    className={classNames?.extra}
                    style={{ flex: 'none', marginInlineStart: token.marginLG, ...styles?.extra }}
                >
                    {extra}
                </div>
            </Flex>
        );
    }

    return (
        <Flex
            align='center'
            className={cx('ant-list-item', className)}
            gap={token.margin}
            justify='space-between'
            style={itemStyle}
            {...props}
        >
            <Flex align='center' flex={1} gap={token.margin} style={{ minWidth: 0, paddingInlineEnd: token.paddingXS }}>
                {children}
            </Flex>
            {actionsContent}
            {extra && (
                <div className={classNames?.extra} style={{ flex: 'none', ...styles?.extra }}>
                    {extra}
                </div>
            )}
        </Flex>
    );
}) as VirtualizedListItemComponent;

const VirtualizedListItemMeta = ({
    avatar,
    children,
    className,
    description,
    style,
    title,
}: VirtualizedListItemMetaProps): JSX.Element => {
    const { token } = useAntDesignTheme();
    const { itemLayout } = useContext(VirtualizedListStyleContext);

    return (
        <Flex
            align='flex-start'
            className={cx('ant-list-item-meta', className)}
            flex={1}
            style={{
                color: token.colorText,
                maxWidth: '100%',
                minWidth: 0,
                ...(itemLayout === 'vertical' ? { marginBlockEnd: token.padding } : undefined),
                ...style,
            }}
        >
            {avatar && (
                <div className='ant-list-item-meta-avatar' style={{ flex: 'none', marginInlineEnd: token.padding }}>
                    {avatar}
                </div>
            )}
            {(title || description || children) && (
                <div
                    className='ant-list-item-meta-content'
                    style={{ color: token.colorText, flex: '1 0', minWidth: 0, width: 0 }}
                >
                    {title && (
                        <h4
                            className='ant-list-item-meta-title'
                            data-table-of-contents-ignore='true'
                            style={{
                                color: token.colorText,
                                fontSize: itemLayout === 'vertical' ? token.fontSizeLG : token.fontSize,
                                lineHeight: itemLayout === 'vertical' ? token.lineHeightLG : token.lineHeight,
                                margin: `0 0 ${unit(itemLayout === 'vertical' ? token.paddingSM : token.marginXXS)} 0`,
                                minWidth: 0,
                            }}
                        >
                            {title}
                        </h4>
                    )}
                    {description && (
                        <div
                            className='ant-list-item-meta-description'
                            style={{
                                color: token.colorTextDescription,
                                fontSize: token.fontSize,
                                lineHeight: token.lineHeight,
                                minWidth: 0,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}
                        >
                            {description}
                        </div>
                    )}
                    {children}
                </div>
            )}
        </Flex>
    );
};

VirtualizedListItem.Meta = VirtualizedListItemMeta;
VirtualizedList.Item = VirtualizedListItem;

export default VirtualizedList;
